import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { useFormat } from 'common/hooks';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { PERFORMANCE_TRENDS_OPTIONS } from 'dashboard409a/common/constants/components/metrics';
import { ComparisonCompanies, MetricCardsRow, RatesOfChange, ValueOverTime } from 'dashboard409a/components';
import { HistoricalData, IGetGpcCompsResponse } from 'dashboard409a/services/types';
import { IGpcCompsState } from 'dashboard409a/states/features/gpcComps';
import { RootStateType } from 'dashboard409a/states/store';
import { getColorFromPalette } from 'dashboard409a/utilities/getColor';
import { ComparisonCompaniesProps } from '../ComparisonCompanies/types';
import { SubscriptionExpiredBox } from '../SubscriptionExpiredBox';

const GPCComp = () => {
  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const { 
    loading, 
    valueOverTime,
    rateOfChange, 
    currentValue,
    medians
  } = useSelector<RootStateType, IGpcCompsState>(state => state.gpcComps);
  const [format, formatDispatch] = useFormat({ units: REGULAR_UNIT });
  const subscriptionIsActive = currentCompany?.is_active;
  const displayExpired = !isUndefined(subscriptionIsActive) && subscriptionIsActive === false;

  const comparisonCompaniesProps: ComparisonCompaniesProps = {
    data: {
      current_value: currentValue || [],
      value_over_time: valueOverTime || [],
      rate_of_change: rateOfChange || [],
      medians: medians || {
        ntm_revenue_multiple: { value: 0, growth_percentage: 0 },
        ntm_revenue_growth: { value: 0, growth_percentage: 0 },
        gross_margin: { value: 0, growth_percentage: 0 },
        ltm_revenue_multiple: { value: 0, growth_percentage: 0 },
        ltm_revenue_growth: { value: 0, growth_percentage: 0 },
        ebitda_margin: { value: 0, growth_percentage: 0 }
      },
    } as IGetGpcCompsResponse,
    isLoading: loading,
    format,
    formatDispatch,
  };

  const valueOverTimeProps = useMemo(
    () => ({
      chartData: valueOverTime?.map((element: HistoricalData, index: number) => ({ ...element, color: getColorFromPalette(index) })) || [],
      isLoading: loading,
    }),
    [valueOverTime, loading]
  );

  const RatesOfChangeProps = useMemo(
    () => {
      const ratesData = rateOfChange?.map((element: HistoricalData, index: number) => ({
        ...element,
        color: getColorFromPalette(index)
      })) || [];

      return {
        chartData: ratesData,
        isLoading: loading,
      };
    },
    [rateOfChange, loading]
  );

  if (displayExpired) {
    return (
      <Grid container spacing={1}>
        <Card>
          <SubscriptionExpiredBox />
        </Card>
      </Grid>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={1}>
        <Card>
          <Grid size={{ xs: 12, lg: 12 }}>
            {medians && <MetricCardsRow medians={medians} />}
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>Multiples</Typography>
                  <ValueOverTime {...valueOverTimeProps} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>Performance Trends</Typography>
                  <RatesOfChange {...RatesOfChangeProps} metricOptions={PERFORMANCE_TRENDS_OPTIONS} />
                </Grid>
              </Grid>
            </CardContent>
          </Grid>

          <Grid size={{ xs: 12, lg: 12 }}>
            <CardContent>
              <ComparisonCompanies {...comparisonCompaniesProps} />
            </CardContent>
          </Grid>
        </Card>
      </Grid>
    </Box>
  );
};

export default GPCComp;
